import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";

import { studentFeeTransactionHistory } from "../../api/Student";
import usePrivateLink from "../config/usePrivateLink";
import { imgBaseUrl } from "../../api/api";

// =========================================================
// HELPERS
// =========================================================

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatAmount = (amount) => {
  const value = Number(amount || 0);

  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getImageUrl = (path) => {
  if (!path) return null;

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  return `${imgBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

const getModuleDetails = (module) => {
  if (!module) return [];

  try {
    const parsed = typeof module === "string" ? JSON.parse(module) : module;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((item) => ({
      type: item?.type || "Unknown",
      amount: Number(item?.amount || 0),
      ids: item?.ids || "",
      allocations: Array.isArray(item?.allocations) ? item.allocations : [],
    }));
  } catch (error) {
    console.error("Invalid module JSON:", error);
    return [];
  }
};

const getTransactionStudent = (item) => {
  // Normal student record
  if (item?.student) {
    return {
      name: item.student.name || "-",
      registration_no: item.student.registration_no || null,
      image: item.student.profile_picture || null,
      student_id: item.student.id || item.student_id || null,
    };
  }

  // Hostel student record
  if (item?.hostel_student) {
    return {
      name: item.hostel_student.student_name || "-",
      registration_no: item.hostel_student.registration_no || null,
      image: null,
      student_id: item.hostel_student.student_id || item.student_id || null,
    };
  }

  // Library student record
  if (item?.library_student) {
    return {
      name:
        item.library_student.student_name || item.library_student.name || "-",
      registration_no: item.library_student.registration_no || null,
      image: item.library_student.profile_picture || null,
      student_id: item.library_student.student_id || item.student_id || null,
    };
  }

  // Bus student record
  if (item?.bus_student) {
    return {
      name: item.bus_student.student_name || item.bus_student.name || "-",
      registration_no: item.bus_student.registration_no || null,
      image: item.bus_student.profile_picture || null,
      student_id: item.bus_student.student_id || item.student_id || null,
    };
  }

  return {
    name: "-",
    registration_no: null,
    image: null,
    student_id: item?.student_id || null,
  };
};

// =========================================================
// ACTION CONFIG
// =========================================================

const getActionConfig = (action) => {
  const value = String(action || "").toLowerCase();

  switch (value) {
    case "created":
      return {
        label: "Payment Requested",
        shortLabel: "Requested",
        icon: "ri-send-plane-fill",

        // Stronger color than bg-warning-subtle
        className: "payment-action-created",

        textClass: "text-warning-emphasis",
      };

    case "approved":
      return {
        label: "Approved",
        shortLabel: "Approved",
        icon: "ri-checkbox-circle-fill",
        className: "bg-success-subtle text-success",
        textClass: "text-success",
      };

    case "rejected":
      return {
        label: "Rejected",
        shortLabel: "Rejected",
        icon: "ri-close-circle-fill",
        className: "bg-danger-subtle text-danger",
        textClass: "text-danger",
      };

    case "cancelled":
      return {
        label: "Cancelled",
        shortLabel: "Cancelled",
        icon: "ri-forbid-2-fill",
        className: "bg-secondary-subtle text-secondary",
        textClass: "text-secondary",
      };

    case "deleted":
      return {
        label: "Deleted",
        shortLabel: "Deleted",
        icon: "ri-delete-bin-fill",
        className: "bg-danger-subtle text-danger",
        textClass: "text-danger",
      };

    default:
      return {
        label: action || "Unknown",
        shortLabel: action || "Unknown",
        icon: "ri-information-fill",
        className: "bg-light text-muted",
        textClass: "text-muted",
      };
  }
};

// =========================================================
// STATUS CONFIG
// =========================================================

const getStatusConfig = (status) => {
  switch (String(status)) {
    case "1":
      return {
        label: "Approved",
        className: "payment-status-approved",
      };

    case "2":
      return {
        label: "Pending",
        className: "payment-status-pending",
      };

    case "3":
      return {
        label: "Rejected",
        className: "payment-status-rejected",
      };

    case "4":
      return {
        label: "Cancelled",
        className: "payment-status-cancelled",
      };

    case "0":
      return {
        label: "Inactive",
        className: "payment-status-inactive",
      };

    default:
      return {
        label: status ?? "-",
        className: "payment-status-inactive",
      };
  }
};

// =========================================================
// ACTION PERSON
// =========================================================

const getActionPerson = (item) => {
  const isCreated = String(item?.action || "").toLowerCase() === "created";

  if (isCreated && item?.action_student) {
    return {
      type: "student",
      label: "Student",
      title: "Requested By",
      name:
        item.action_student.name ||
        item.action_student.student_name ||
        `Student #${item.action_by || "-"}`,
      image: getImageUrl(item.action_student.profile_picture),
      registration:
        item.action_student.registration_no ||
        item.action_student.admission_no ||
        null,
      id: item.action_student.id || item.action_by,
    };
  }

  if (!isCreated && item?.action_staff) {
    return {
      type: "staff",
      label: "Staff",
      title: "Processed By",
      name:
        item.action_staff.name ||
        item.action_staff.staff_name ||
        `Staff #${item.action_by || "-"}`,
      image: getImageUrl(item.action_staff.img),
      registration: item.action_staff.registration_no || null,
      id: item.action_staff.id || item.action_by,
    };
  }

  return null;
};

// =========================================================
// COMPONENT
// =========================================================

const PaymentTransactionHistory = () => {
  const privateLink = usePrivateLink();

  // -------------------------------------------------------
  // STATE
  // -------------------------------------------------------

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: 0,
    to: 0,
  });

  // =======================================================
  // FETCH HISTORY
  // =======================================================

  const fetchHistory = async (page = 1) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      params.append("page", page);
      params.append("per_page", pagination.per_page || 20);

      if (actionFilter) {
        params.append("action", actionFilter);
      }

      if (statusFilter) {
        params.append("status", statusFilter);
      }

      const response = await studentFeeTransactionHistory(params.toString());

      if (response?.data?.status) {
        const result = response.data;

        setHistory(Array.isArray(result.data) ? result.data : []);

        setPagination(
          result.pagination || {
            current_page: page,
            last_page: 1,
            per_page: 20,
            total: 0,
            from: 0,
            to: 0,
          },
        );
      } else {
        setHistory([]);

        Swal.fire({
          icon: "error",
          title: "Unable to load history",
          text:
            response?.data?.message ||
            "Payment transaction history could not be loaded.",
        });
      }
    } catch (error) {
      console.error("Payment transaction history error:", error);

      setHistory([]);

      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text:
          error?.response?.data?.message ||
          "Unable to fetch payment transaction history.",
      });
    } finally {
      setLoading(false);
    }
  };

  // =======================================================
  // INITIAL FETCH / FILTER FETCH
  // =======================================================

  useEffect(() => {
    fetchHistory(1);
  }, [actionFilter, statusFilter]);

  // =======================================================
  // SEARCH
  // =======================================================

  const filteredHistory = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) {
      return history;
    }

    return history.filter((item) => {
      const actionPerson = getActionPerson(item);

      const searchableText = [
        item.student_id,
        item.payment_transaction_id,
        item.receipt_no,
        item.transaction_no,
        item.account_no,
        item.remark,
        item.action,
        item.action_by,
        item.action_remark,
        item.amount,
        item.module,
        item.payment_method,
        item.bank_id,

        // Action person
        actionPerson?.name,
        actionPerson?.registration,
        actionPerson?.type,
      ]
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value).toLowerCase())
        .join(" ");

      return searchableText.includes(searchValue);
    });
  }, [history, search]);

  // =======================================================
  // SUMMARY
  // =======================================================

  const createdCount = history.filter(
    (item) => String(item.action).toLowerCase() === "created",
  ).length;

  const approvedCount = history.filter(
    (item) => String(item.action).toLowerCase() === "approved",
  ).length;

  // =======================================================
  // CLEAR FILTERS
  // =======================================================

  const clearFilters = () => {
    setSearch("");
    setActionFilter("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  // =======================================================
  // PAGE CHANGE
  // =======================================================

  const changePage = (page) => {
    if (
      page < 1 ||
      page > pagination.last_page ||
      page === pagination.current_page
    ) {
      return;
    }

    setCurrentPage(page);

    fetchHistory(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================================================
  // PAGINATION
  // =======================================================

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) {
      return null;
    }

    const current = pagination.current_page;
    const last = pagination.last_page;

    const pages = [];

    let start = Math.max(1, current - 2);
    let end = Math.min(last, current + 2);

    if (current <= 3) {
      end = Math.min(last, 5);
    }

    if (current >= last - 2) {
      start = Math.max(1, last - 4);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
        <div className="text-muted small">
          Showing <strong>{pagination.from || 0}</strong> to{" "}
          <strong>{pagination.to || 0}</strong> of{" "}
          <strong>{pagination.total || 0}</strong> records
        </div>

        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${current <= 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => changePage(current - 1)}
                disabled={current <= 1}
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
            </li>

            {pages.map((page) => (
              <li
                key={page}
                className={`page-item ${page === current ? "active" : ""}`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => changePage(page)}
                >
                  {page}
                </button>
              </li>
            ))}

            <li className={`page-item ${current >= last ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => changePage(current + 1)}
                disabled={current >= last}
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <>
      <style>
        {`
          /* =====================================================
             PAYMENT HISTORY UI
          ===================================================== */

          .payment-history-table th {
            white-space: nowrap;
            font-size: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .02em;
          }

          .payment-history-table td {
            font-size: 13px;
          }

          .payment-action-created {
  background: #fff3cd !important;
  color: #8a4b00 !important;
  border: 1px solid #ffca5c !important;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(138, 75, 0, 0.08);
}

.payment-action-created i {
  color: #d97706 !important;
}

          .action-person-avatar {
            width: 38px;
            height: 38px;
            min-width: 38px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid rgba(0, 0, 0, 0.08);
          }

          .action-person-avatar-placeholder {
            width: 38px;
            height: 38px;
            min-width: 38px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 17px;
          }

          .transaction-image-preview {
  display: block;
  width: 100%;
  max-width: 700px;
  max-height: 450px;
  margin: 0 auto;
  object-fit: contain;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 6px;
}

          .detail-card {
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: 12px;
            padding: 14px 16px;
            height: 100%;
          }

          .detail-card-label {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .04em;
            color: #8a8f98;
            margin-bottom: 5px;
          }

          .detail-card-value {
            font-size: 14px;
            font-weight: 600;
          }

          .action-person-detail {
            border-radius: 14px;
            padding: 15px;
            background: rgba(13, 110, 253, 0.04);
            border: 1px solid rgba(13, 110, 253, 0.10);
          }

          .transaction-image-box {
  border-radius: 14px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

          @media (max-width: 768px) {
            .payment-history-table {
              min-width: 1050px;
            }
          }

          .payment-module-chip {
  display: inline-flex;
  align-items: center;
  max-width: 180px;
  padding: 4px 9px;
  border-radius: 6px;
  background: rgba(13, 110, 253, 0.08);
  color: var(--bs-primary);
  border: 1px solid rgba(13, 110, 253, 0.15);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.payment-module-chip i {
  font-size: 12px;
}

.payment-module-detail {
  padding: 15px;
  border-radius: 14px;
  background: rgba(13, 110, 253, 0.04);
  border: 1px solid rgba(13, 110, 253, 0.12);
}

.payment-module-icon {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(13, 110, 253, 0.10);
  color: var(--bs-primary);
  font-size: 18px;
}

.payment-module-detail-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 13px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* =====================================================
   PAYMENT STATUS CHIPS
===================================================== */

.payment-status-pending {
  background: #fff0c2 !important;
  color: #7a4b00 !important;
  border: 1px solid #e6b84f !important;
  font-weight: 700 !important;
}

.payment-status-pending i {
  color: #b56a00 !important;
}


/* APPROVED */

.payment-status-approved {
  background: #d9f5e5 !important;
  color: #146c43 !important;
  border: 1px solid #8ed5ad !important;
  font-weight: 700 !important;
}

.payment-status-approved i {
  color: #198754 !important;
}


/* REJECTED */

.payment-status-rejected {
  background: #f8d7da !important;
  color: #842029 !important;
  border: 1px solid #ea9da4 !important;
  font-weight: 700 !important;
}

.payment-status-rejected i {
  color: #dc3545 !important;
}


/* CANCELLED */

.payment-status-cancelled {
  background: #e2e3e5 !important;
  color: #41464b !important;
  border: 1px solid #bfc2c5 !important;
  font-weight: 700 !important;
}

.payment-status-cancelled i {
  color: #6c757d !important;
}


/* INACTIVE */

.payment-status-inactive {
  background: #e9ecef !important;
  color: #495057 !important;
  border: 1px solid #ced4da !important;
  font-weight: 700 !important;
}

        `}
      </style>

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* =================================================
                        PAGE HEADER
                    ================================================= */}

                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{
                              width: 46,
                              height: 46,
                            }}
                          >
                            <i className="ri-history-line"></i>
                          </div>

                          <div>
                            <h5 className="m-0 fw-bold">
                              Payment Transaction History
                            </h5>

                            <small className="text-muted">
                              Complete audit history of fee payment requests
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        SUMMARY CARDS
                    ================================================= */}

                    <div className="row g-3 mb-3">
                      {/* TOTAL */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="text-muted small mb-1">
                                  Total Records
                                </div>

                                <h4 className="fw-bold mb-0">
                                  {pagination.total || 0}
                                </h4>
                              </div>

                              <div
                                className="bg-primary-subtle text-primary rounded-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: 44,
                                  height: 44,
                                }}
                              >
                                <i className="ri-file-list-3-line fs-5"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* PAYMENT REQUESTS */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="text-muted small mb-1">
                                  Payment Requests
                                </div>

                                <h4 className="fw-bold mb-0">{createdCount}</h4>
                              </div>

                              <div
                                className="payment-action-created rounded-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: 44,
                                  height: 44,
                                }}
                              >
                                <i className="ri-send-plane-fill fs-5"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* APPROVED */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="text-muted small mb-1">
                                  Approved
                                </div>

                                <h4 className="fw-bold mb-0">
                                  {approvedCount}
                                </h4>
                              </div>

                              <div
                                className="bg-success-subtle text-success rounded-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: 44,
                                  height: 44,
                                }}
                              >
                                <i className="ri-checkbox-circle-line fs-5"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CURRENT PAGE */}
                      <div className="col-xl-3 col-md-6">
                        <div className="card border-0 shadow-sm rounded-4 h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <div className="text-muted small mb-1">
                                  Current Page
                                </div>

                                <h4 className="fw-bold mb-0">
                                  {pagination.current_page || 1}
                                </h4>
                              </div>

                              <div
                                className="bg-info-subtle text-info rounded-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: 44,
                                  height: 44,
                                }}
                              >
                                <i className="ri-pages-line fs-5"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        FILTERS
                    ================================================= */}

                    <div className="card border-0 shadow-sm rounded-4 mb-3">
                      <div className="card-body">
                        <div className="row g-3 align-items-end">
                          {/* SEARCH */}
                          <div className="col-xl-5 col-lg-5">
                            <label className="form-label fw-semibold small">
                              Search History
                            </label>

                            <div className="input-group">
                              <span className="input-group-text bg-transparent">
                                <i className="ri-search-line"></i>
                              </span>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search transaction, receipt, student, amount..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* ACTION */}
                          <div className="col-xl-2 col-lg-2 col-md-4">
                            <label className="form-label fw-semibold small">
                              Action
                            </label>

                            <select
                              className="form-select"
                              value={actionFilter}
                              onChange={(e) => {
                                setActionFilter(e.target.value);
                                setCurrentPage(1);
                              }}
                            >
                              <option value="">All Actions</option>
                              <option value="created">Payment Requested</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="cancelled">Cancelled</option>
                              <option value="deleted">Deleted</option>
                            </select>
                          </div>

                          {/* STATUS */}
                          <div className="col-xl-2 col-lg-2 col-md-4">
                            <label className="form-label fw-semibold small">
                              Status
                            </label>

                            <select
                              className="form-select"
                              value={statusFilter}
                              onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                              }}
                            >
                              <option value="">All Status</option>
                              <option value="1">Approved</option>
                              <option value="2">Pending</option>
                              <option value="3">Rejected</option>
                              <option value="4">Cancelled</option>
                              <option value="0">Inactive</option>
                            </select>
                          </div>

                          {/* CLEAR */}
                          <div className="col-xl-3 col-lg-3 col-md-4">
                            <button
                              type="button"
                              className="btn btn-light border w-100 d-flex align-items-center justify-content-center gap-2"
                              onClick={clearFilters}
                            >
                              <i className="ri-refresh-line"></i>
                              Clear Filters
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        HISTORY TABLE
                    ================================================= */}

                    <div className="card border-0 shadow-sm rounded-4">
                      <div className="card-body p-0">
                        <div className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
                          <div>
                            <h6 className="fw-bold mb-1">
                              Transaction Activity
                            </h6>

                            <small className="text-muted">
                              Every record represents a change or action
                              performed on a fee transaction.
                            </small>
                          </div>

                          {!loading && (
                            <span className="badge bg-light text-dark border">
                              {pagination.total || 0} Records
                            </span>
                          )}
                        </div>

                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0 payment-history-table">
                            <thead className="table-light">
                              <tr>
                                <th className="px-3">#</th>
                                <th>Transaction</th>
                                <th>Student</th>
                                <th>Amount</th>
                                <th>Action</th>
                                <th>Status</th>
                                <th>Action By</th>
                                <th>Date & Time</th>
                                <th className="text-end px-3">Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* LOADING */}
                              {loading ? (
                                <tr>
                                  <td colSpan="9" className="text-center py-5">
                                    <div
                                      className="spinner-border text-primary mb-3"
                                      role="status"
                                    ></div>

                                    <div className="text-muted">
                                      Loading transaction history...
                                    </div>
                                  </td>
                                </tr>
                              ) : filteredHistory.length === 0 ? (
                                /* EMPTY */
                                <tr>
                                  <td colSpan="9" className="text-center py-5">
                                    <div
                                      className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                                      style={{
                                        width: 64,
                                        height: 64,
                                      }}
                                    >
                                      <i className="ri-history-line fs-3 text-muted"></i>
                                    </div>

                                    <h6 className="fw-bold mb-1">
                                      No History Found
                                    </h6>

                                    <p className="text-muted small mb-0">
                                      No payment transaction history matches the
                                      selected filters.
                                    </p>
                                  </td>
                                </tr>
                              ) : (
                                filteredHistory.map((item, index) => {
                                  const action = getActionConfig(item.action);

                                  const status = getStatusConfig(item.status);

                                  const actionPerson = getActionPerson(item);

                                  const transactionStudent =
                                    getTransactionStudent(item);

                                  const isCreated =
                                    String(item.action || "").toLowerCase() ===
                                    "created";

                                  return (
                                    <tr
                                      key={
                                        item.id ||
                                        `${item.payment_transaction_id}-${index}`
                                      }
                                    >
                                      {/* NUMBER */}
                                      <td className="px-3">
                                        <span className="text-muted small">
                                          {(pagination.current_page - 1) *
                                            pagination.per_page +
                                            index +
                                            1}
                                        </span>
                                      </td>

                                      {/* TRANSACTION */}
                                      <td>
                                        {item.receipt_no && (
                                          <div className="small text-muted mt-1">
                                            <i className="ri-receipt-line me-1"></i>
                                            {item.receipt_no}
                                          </div>
                                        )}

                                        {item.transaction_no && (
                                          <div className="small text-muted mt-1">
                                            <i className="ri-bank-card-line me-1"></i>
                                            {item.transaction_no}
                                          </div>
                                        )}

                                        {getModuleDetails(item.module).length >
                                          0 && (
                                          <div className="mt-2">
                                            {getModuleDetails(item.module).map(
                                              (module, moduleIndex) => (
                                                <span
                                                  key={`${module.type}-${moduleIndex}`}
                                                  className="payment-module-chip"
                                                >
                                                  <i className="ri-bookmark-3-line me-1"></i>
                                                  {module.type}
                                                </span>
                                              ),
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          {transactionStudent.image ? (
                                            <img
                                              src={getImageUrl(
                                                transactionStudent.image,
                                              )}
                                              alt={
                                                transactionStudent.name ||
                                                "Student"
                                              }
                                              className="action-person-avatar"
                                            />
                                          ) : (
                                            <div className="action-person-avatar-placeholder bg-primary-subtle text-primary">
                                              <i className="ri-user-line"></i>
                                            </div>
                                          )}

                                          <div>
                                            <div className="fw-semibold">
                                              {transactionStudent.name}
                                            </div>

                                            {transactionStudent.registration_no && (
                                              <div className="small text-muted">
                                                Reg:{" "}
                                                {
                                                  transactionStudent.registration_no
                                                }
                                              </div>
                                            )}

                                            {transactionStudent.student_id && (
                                              <div className="small text-muted">
                                                Student ID:{" "}
                                                {transactionStudent.student_id}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>

                                      {/* AMOUNT */}
                                      <td>
                                        <span className="fw-bold">
                                          ₹{formatAmount(item.amount)}
                                        </span>

                                        {item.payment_method && (
                                          <div className="small text-muted mt-1">
                                            {item.payment_method}
                                          </div>
                                        )}
                                      </td>

                                      {/* ACTION */}
                                      <td>
                                        <span
                                          className={`badge rounded-pill px-2 py-1 ${action.className}`}
                                        >
                                          <i
                                            className={`${action.icon} me-1`}
                                          ></i>

                                          {action.label}
                                        </span>
                                      </td>

                                      {/* STATUS */}
                                      <td>
                                        <span
                                          className={`badge rounded-pill px-2 py-1 ${status.className}`}
                                        >
                                          {status.label === "Pending" && (
                                            <i className="ri-time-line me-1"></i>
                                          )}

                                          {status.label === "Approved" && (
                                            <i className="ri-checkbox-circle-line me-1"></i>
                                          )}

                                          {status.label === "Rejected" && (
                                            <i className="ri-close-circle-line me-1"></i>
                                          )}

                                          {status.label === "Cancelled" && (
                                            <i className="ri-forbid-2-line me-1"></i>
                                          )}

                                          {status.label === "Inactive" && (
                                            <i className="ri-pause-circle-line me-1"></i>
                                          )}

                                          {status.label}
                                        </span>
                                      </td>

                                      {/* ACTION BY */}
                                      <td>
                                        {actionPerson ? (
                                          <div className="d-flex align-items-center gap-2">
                                            {actionPerson.image ? (
                                              <img
                                                src={actionPerson.image}
                                                alt={actionPerson.name}
                                                className="action-person-avatar"
                                              />
                                            ) : (
                                              <div
                                                className={`action-person-avatar-placeholder ${
                                                  actionPerson.type ===
                                                  "student"
                                                    ? "bg-primary-subtle text-primary"
                                                    : "bg-info-subtle text-info"
                                                }`}
                                              >
                                                <i
                                                  className={
                                                    actionPerson.type ===
                                                    "student"
                                                      ? "ri-user-line"
                                                      : "ri-user-settings-line"
                                                  }
                                                ></i>
                                              </div>
                                            )}

                                            <div>
                                              <div className="d-flex align-items-center gap-1">
                                                <span className="fw-semibold">
                                                  {actionPerson.name}
                                                </span>
                                              </div>

                                              <div className="small text-muted">
                                                <span
                                                  className={
                                                    actionPerson.type ===
                                                    "student"
                                                      ? "text-primary"
                                                      : "text-info"
                                                  }
                                                >
                                                  {actionPerson.label}
                                                </span>

                                                {actionPerson.registration && (
                                                  <>
                                                    {" "}
                                                    ·{" "}
                                                    {actionPerson.registration}
                                                  </>
                                                )}
                                              </div>

                                              {item.action_remark && (
                                                <div
                                                  className="small text-muted text-truncate mt-1"
                                                  style={{
                                                    maxWidth: 210,
                                                  }}
                                                  title={item.action_remark}
                                                >
                                                  <i className="ri-chat-quote-line me-1"></i>
                                                  {item.action_remark}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ) : item.action_by ? (
                                          <div className="small text-muted">
                                            <i className="ri-user-line me-1"></i>
                                            ID #{item.action_by}
                                          </div>
                                        ) : (
                                          <span className="text-muted small">
                                            <i className="ri-cpu-line me-1"></i>
                                            System / App
                                          </span>
                                        )}
                                      </td>

                                      {/* DATE */}
                                      <td>
                                        <div className="fw-medium">
                                          {formatDateTime(item.action_at)}
                                        </div>

                                        {item.payment_date && (
                                          <div className="small text-muted mt-1">
                                            Payment:{" "}
                                            {formatDate(item.payment_date)}
                                          </div>
                                        )}
                                      </td>

                                      {/* DETAILS */}
                                      <td className="text-end px-3">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-light border"
                                          title="View transaction details"
                                          onClick={() =>
                                            setSelectedTransaction(item)
                                          }
                                          data-bs-toggle="modal"
                                          data-bs-target="#transactionHistoryDetailsModal"
                                        >
                                          <i className="ri-eye-line"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="px-3 pb-3">{renderPagination()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* =====================================================
                  DETAIL MODAL
              ===================================================== */}

              <div
                className="modal fade"
                id="transactionHistoryDetailsModal"
                tabIndex="-1"
                aria-hidden="true"
              >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content border-0 rounded-4 shadow">
                    {selectedTransaction && (
                      <>
                        {(() => {
                          const action = getActionConfig(
                            selectedTransaction.action,
                          );

                          const status = getStatusConfig(
                            selectedTransaction.status,
                          );

                          const actionPerson =
                            getActionPerson(selectedTransaction);

                          const isCreated =
                            String(
                              selectedTransaction.action || "",
                            ).toLowerCase() === "created";

                          return (
                            <>
                              {/* =================================================
                                  MODAL HEADER
                              ================================================= */}

                              <div className="modal-header border-bottom">
                                <div>
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <h5 className="modal-title fw-bold mb-0">
                                      Transaction Details
                                    </h5>

                                    <span
                                      className={`badge rounded-pill ${action.className}`}
                                    >
                                      <i className={`${action.icon} me-1`}></i>
                                      {action.label}
                                    </span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="btn-close"
                                  data-bs-dismiss="modal"
                                  aria-label="Close"
                                  onClick={() => setSelectedTransaction(null)}
                                ></button>
                              </div>

                              {/* =================================================
                                  MODAL BODY
                              ================================================= */}

                              <div className="modal-body">
                                {/* TOP SUMMARY */}
                                <div className="row g-3 mb-3">
                                  {/* AMOUNT */}
                                  <div className="col-md-4">
                                    <div className="detail-card bg-light">
                                      <div className="detail-card-label">
                                        Amount
                                      </div>

                                      <div className="fs-5 fw-bold">
                                        ₹
                                        {formatAmount(
                                          selectedTransaction.amount,
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ACTION */}
                                  <div className="col-md-4">
                                    <div className="detail-card bg-light">
                                      <div className="detail-card-label">
                                        Action
                                      </div>

                                      <span
                                        className={`badge rounded-pill ${action.className}`}
                                      >
                                        <i
                                          className={`${action.icon} me-1`}
                                        ></i>
                                        {action.label}
                                      </span>
                                    </div>
                                  </div>

                                  {/* STATUS */}
                                  <div className="col-md-4">
                                    <div className="detail-card bg-light">
                                      <div className="detail-card-label">
                                        Status
                                      </div>

                                      <span
                                        className={`badge rounded-pill ${status.className}`}
                                      >
                                        {status.label}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* =================================================
    PAYMENT MODULE
================================================= */}

                                {getModuleDetails(selectedTransaction.module)
                                  .length > 0 && (
                                  <div className="payment-module-detail mb-3">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <div className="payment-module-icon">
                                        <i className="ri-bookmark-3-fill"></i>
                                      </div>

                                      <div>
                                        <div className="fw-bold">
                                          Payment For
                                        </div>

                                        <small className="text-muted">
                                          Fee/module covered by this transaction
                                        </small>
                                      </div>
                                    </div>

                                    <div className="row g-2">
                                      {getModuleDetails(
                                        selectedTransaction.module,
                                      ).map((module, moduleIndex) => (
                                        <div
                                          className="col-md-6"
                                          key={`${module.type}-${moduleIndex}`}
                                        >
                                          <div className="payment-module-detail-item">
                                            <div>
                                              <div className="fw-semibold">
                                                {module.type}
                                              </div>
                                            </div>

                                            <div className="fw-bold text-primary">
                                              ₹{formatAmount(module.amount)}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* =================================================
                                    ACTION PERSON
                                ================================================= */}

                                {actionPerson && (
                                  <div className="action-person-detail mb-3">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                      <div>
                                        <div className="small text-muted mb-1">
                                          {isCreated
                                            ? "Payment Requested By"
                                            : "Action Processed By"}
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                          {actionPerson.image ? (
                                            <img
                                              src={actionPerson.image}
                                              alt={actionPerson.name}
                                              style={{
                                                width: 58,
                                                height: 58,
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                border:
                                                  "2px solid rgba(0,0,0,.08)",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              className={`d-flex align-items-center justify-content-center rounded-circle ${
                                                actionPerson.type === "student"
                                                  ? "bg-primary-subtle text-primary"
                                                  : "bg-info-subtle text-info"
                                              }`}
                                              style={{
                                                width: 58,
                                                height: 58,
                                                fontSize: 24,
                                              }}
                                            >
                                              <i
                                                className={
                                                  actionPerson.type ===
                                                  "student"
                                                    ? "ri-user-line"
                                                    : "ri-user-settings-line"
                                                }
                                              ></i>
                                            </div>
                                          )}

                                          <div>
                                            <div className="fw-bold fs-6">
                                              {actionPerson.name}
                                            </div>

                                            <div className="d-flex align-items-center gap-2 mt-1">
                                              <span
                                                className={`badge ${
                                                  actionPerson.type ===
                                                  "student"
                                                    ? "bg-primary-subtle text-primary"
                                                    : "bg-info-subtle text-info"
                                                }`}
                                              >
                                                {actionPerson.label}
                                              </span>

                                              {actionPerson.registration && (
                                                <small className="text-muted">
                                                  {actionPerson.registration}
                                                </small>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {selectedTransaction.action_remark && (
                                      <div className="border-top mt-3 pt-3">
                                        <div className="small text-muted mb-1">
                                          Action Remark
                                        </div>

                                        <div className="small">
                                          <i className="ri-chat-quote-line me-1"></i>
                                          {selectedTransaction.action_remark}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* =================================================
                                    TRANSACTION IMAGE
                                ================================================= */}

                                {selectedTransaction.image && (
                                  <div className="transaction-image-box mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <div>
                                        <div className="fw-semibold">
                                          Payment Proof
                                        </div>

                                        <small className="text-muted">
                                          Image uploaded with this payment
                                          transaction
                                        </small>
                                      </div>

                                      <a
                                        href={getImageUrl(
                                          selectedTransaction.image,
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-light border"
                                      >
                                        <i className="ri-external-link-line me-1"></i>
                                        Open Image
                                      </a>
                                    </div>

                                    <div className="text-center">
                                      <img
                                        src={getImageUrl(
                                          selectedTransaction.image,
                                        )}
                                        alt="Payment transaction proof"
                                        className="transaction-image-preview"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* =================================================
                                    TRANSACTION INFORMATION
                                ================================================= */}

                                <div className="row g-3">
                                  {/* RECEIPT */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Receipt Number
                                      </div>

                                      <div className="detail-card-value">
                                        {selectedTransaction.receipt_no ||
                                          "Not generated"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* PAYMENT METHOD */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Payment Method
                                      </div>

                                      <div className="detail-card-value">
                                        {selectedTransaction.payment_method ||
                                          "-"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* TRANSACTION NUMBER */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Transaction Number
                                      </div>

                                      <div className="detail-card-value text-break">
                                        {selectedTransaction.transaction_no ||
                                          "-"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ACCOUNT */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Account
                                      </div>

                                      <div className="detail-card-value">
                                        {selectedTransaction.account_no || "-"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* BANK */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Bank
                                      </div>

                                      <div className="detail-card-value">
                                        {selectedTransaction.bank?.owner_name ||
                                          selectedTransaction.bank
                                            ?.account_no ||
                                          "-"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ACTION DATE */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Action Date
                                      </div>

                                      <div className="detail-card-value">
                                        {formatDateTime(
                                          selectedTransaction.action_at,
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* PAYMENT DATE */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Payment Date
                                      </div>

                                      <div className="detail-card-value">
                                        {formatDate(
                                          selectedTransaction.payment_date,
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* TRANSACTION CREATED */}
                                  <div className="col-md-6">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Transaction Created
                                      </div>

                                      <div className="detail-card-value">
                                        {formatDateTime(
                                          selectedTransaction.transaction_created_at,
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* TRANSACTION REMARK */}
                                  <div className="col-12">
                                    <div className="detail-card">
                                      <div className="detail-card-label">
                                        Transaction Remark
                                      </div>

                                      <div className="detail-card-value fw-normal">
                                        {selectedTransaction.remark ||
                                          "No remark"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* =================================================
                                  MODAL FOOTER
                              ================================================= */}

                              <div className="modal-footer border-top">
                                <button
                                  type="button"
                                  className="btn btn-light border"
                                  data-bs-dismiss="modal"
                                  onClick={() => setSelectedTransaction(null)}
                                >
                                  Close
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentTransactionHistory;
